import { Button } from '@economic/taco';
import { Trans } from '@lingui/macro';
import { FormApi } from 'final-form';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Field } from '~/app/shared/components/Form';
import AmountInput from '~/app/shared/components/ui/AmountInput';
import number from '~/app/shared/util/utilities/number';

interface Props {
    quantity: number;
    alreadyInvoiced: number;
    form: FormApi;
    fieldId: string;
    fieldValue: number | undefined;
}

export default function QuantityForInvoicingInputHandler({ quantity, alreadyInvoiced, form, fieldId, fieldValue }: Props) {
    const fieldValueRef = useRef<string | undefined>();

    // workaround to ensure data is not lost due to cell being unmounted on scroll in a virtualized table
    useEffect(() => {
        return () => {
            if (fieldValueRef.current === undefined) {
                return;
            }

            const newFieldValue = number.parse(fieldValueRef.current);
            if (fieldValue !== newFieldValue) {
                form.change(fieldId, newFieldValue);
                form.blur(fieldId);
            }

            fieldValueRef.current = undefined;
        };
    }, [form, fieldId, fieldValue]);

    // keep current input in ref to be able to commit it in cleanup in case of unmount
    const handleOnKeyUp = useCallback((e: { target: { value: string | null } }) => {
        fieldValueRef.current = e.target.value ?? undefined;
    }, []);

    const postfix = useMemo(
        () =>
            quantity - alreadyInvoiced > 0 ? (
                <Button
                    appearance="discrete"
                    onClick={() => {
                        form.change(fieldId, quantity - alreadyInvoiced);
                    }}>
                    <Trans>balance</Trans>
                </Button>
            ) : undefined,
        [quantity, alreadyInvoiced, form, fieldId]
    );

    return (
        <div className="w-full">
            <Field.Number
                hideLabel
                name={fieldId}
                disabled={quantity - alreadyInvoiced <= 0}
                as={AmountInput}
                fieldClassName="w-full"
                defaultValue={fieldValue}
                onKeyUp={handleOnKeyUp}
                postfix={postfix}
            />
        </div>
    );
}
